import React, { useEffect, useMemo, useState } from "react";
import { CheckSquare, ListTodo, AlertTriangle, ArrowUpRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import TaskModal from "@/components/tasks/TaskModal";
import { StatusBadge, PriorityBadge, STATUS_META } from "@/components/tasks/taskMeta";
import { useNavigate } from "react-router-dom";

export default function TasksList({ refreshKey, onChange }) {
  const [tasks, setTasks] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState({ open: false, initial: null });
  const navigate = useNavigate();

  const load = () => endpoints.listTasks().then(setTasks).catch(() => {});

  useEffect(() => { load(); endpoints.listConsultants().then(setConsultants).catch(() => {}); }, []);
  useEffect(() => { load(); }, [refreshKey]);

  const filtered = useMemo(() => {
    let list = [...tasks];
    if (filter === "overdue") list = list.filter((t) => t.status === "Overdue");
    else if (filter === "progress") list = list.filter((t) => t.status === "In Progress");
    else if (filter === "high") list = list.filter((t) => t.priority === "High" && t.status !== "Completed");
    else if (filter === "done") list = list.filter((t) => t.status === "Completed");
    else list = list.filter((t) => t.status !== "Completed");
    return list.sort((a, b) => {
      const aOver = a.status === "Overdue" ? 0 : 1;
      const bOver = b.status === "Overdue" ? 0 : 1;
      if (aOver !== bOver) return aOver - bOver;
      return (b.priority === "High" ? 1 : 0) - (a.priority === "High" ? 1 : 0);
    });
  }, [tasks, filter]);

  const overdueCount = tasks.filter((t) => t.status === "Overdue").length;

  const toggle = async (t, e) => {
    e.stopPropagation();
    const nextStatus = t.status === "Completed" ? "In Progress" : "Completed";
    setTasks((prev) => prev.map((x) => x.id === t.id ? { ...x, status: nextStatus, completed: nextStatus === "Completed", progress: nextStatus === "Completed" ? 100 : x.progress } : x));
    try {
      await endpoints.updateTask(t.id, { status: nextStatus });
      toast.success(nextStatus === "Completed" ? "Task marked complete" : "Task reopened");
      onChange?.();
    } catch {
      toast.error("Could not update task");
      load();
    }
  };

  return (
    <div data-testid="tasks-list-card" className="glass-card rounded-3xl p-5 sm:p-6 h-full card-hover">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 flex items-center justify-center">
          <ListTodo className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="font-display font-bold text-[15px] tracking-tight">Tasks</h2>
            {overdueCount > 0 && (
              <button
                data-testid="tasks-overdue-alert"
                onClick={() => setFilter("overdue")}
                className="inline-flex items-center gap-1 rounded-full bg-rose-100 dark:bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-500/25 px-2 py-0.5 text-[10px] font-semibold hover:brightness-95 transition"
              >
                <AlertTriangle className="h-2.5 w-2.5" />
                {overdueCount} overdue
              </button>
            )}
          </div>
          <p className="text-xs text-slate-500">Click a task to open</p>
        </div>
        <button
          data-testid="tasks-view-all"
          onClick={() => navigate("/tasks")}
          className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
        >
          View all <ArrowUpRight className="h-3 w-3" />
        </button>
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-3">
        <TabsList data-testid="tasks-filter-tabs" className="h-8 bg-slate-100 dark:bg-slate-800 w-full flex">
          <TabsTrigger value="all" className="text-xs h-6 flex-1" data-testid="tasks-filter-all">All open</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="tasks-filter-overdue" className="text-xs h-6 flex-1 data-[state=active]:bg-rose-500 data-[state=active]:text-white">Overdue</TabsTrigger>
          <TabsTrigger value="progress" className="text-xs h-6 flex-1" data-testid="tasks-filter-progress">In progress</TabsTrigger>
          <TabsTrigger value="high" className="text-xs h-6 flex-1" data-testid="tasks-filter-high">High</TabsTrigger>
          <TabsTrigger value="done" className="text-xs h-6 flex-1" data-testid="tasks-filter-done">Done</TabsTrigger>
        </TabsList>
      </Tabs>

      {filtered.length === 0 ? (
        <div className="text-center py-10 text-sm text-slate-500">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
            <CheckSquare className="h-5 w-5 text-slate-400" />
          </div>
          You&apos;re all caught up. Create a task from Quick Actions.
        </div>
      ) : (
        <ScrollArea className="h-[300px] pr-2">
          <ul className="space-y-2.5">
            {filtered.map((t) => {
              const isOverdue = t.status === "Overdue";
              return (
                <li
                  key={t.id}
                  data-testid={`task-item-${t.id}`}
                  onClick={() => setModal({ open: true, initial: t })}
                  className={`group flex items-start gap-3 rounded-2xl border p-3 cursor-pointer transition-all
                    ${isOverdue
                      ? "bg-rose-50/70 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/25 hover:border-rose-300"
                      : "bg-white/60 dark:bg-slate-900/40 border-slate-200/70 dark:border-white/10 hover:border-indigo-300/70"
                    }`}
                >
                  <Checkbox
                    data-testid={`task-item-checkbox-${t.id}`}
                    checked={t.status === "Completed"}
                    onCheckedChange={() => {}}
                    onClick={(e) => toggle(t, e)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-semibold truncate ${t.status === "Completed" ? "line-through text-slate-400" : ""}`}>{t.title}</span>
                      <StatusBadge status={t.status} />
                      <PriorityBadge priority={t.priority} />
                    </div>
                    <div className="flex items-center gap-3 text-[11px] text-slate-500 mt-1 flex-wrap">
                      {t.assignee && <span className="truncate">👤 {t.assignee}</span>}
                      {t.client && <span className="truncate">📁 {t.client}</span>}
                      {t.due_date && (
                        <span className={isOverdue ? "font-semibold text-rose-600" : ""}>
                          Due {new Date(t.due_date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                        </span>
                      )}
                      <span className="ml-auto font-mono">{t.progress}%</span>
                    </div>
                    <Progress value={t.progress || 0} className="h-1.5 mt-2" />
                  </div>
                </li>
              );
            })}
          </ul>
        </ScrollArea>
      )}

      <TaskModal
        open={modal.open}
        initial={modal.initial}
        consultants={consultants}
        onOpenChange={(o) => setModal({ open: o, initial: modal.initial })}
        onChanged={() => { load(); onChange?.(); setModal({ open: false, initial: null }); }}
      />
    </div>
  );
}
